import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle, ChevronRight, Clock, Download, Eye, ExternalLink, File, FileSpreadsheet, FileText, Film, Folder,
  Image, Link2, Loader2, Music, Presentation, RefreshCw, Search, Upload, X,
} from 'lucide-react';
import { apiService } from '../../services/api';
import DriveBrowserPanel from '../ProjectDetail/panels/DriveBrowserPanel';

// Fichiers du Drive du projet lus dans la copie du stockage Innowide (Scaleway, Paris) : navigation, aperçu,
// téléchargement direct et dépôt (le fichier part dans le stockage puis dans le Drive). Projet hors copie (clos, sans
// dossier) ou copie coupée : l'ancien navigateur Google Drive.

const size = (value) => {
  if (!value) return '';
  if (value < 1024) return `${value} o`;
  if (value < 1024 ** 2) return `${Math.round(value / 1024)} Ko`;
  if (value < 1024 ** 3) return `${(value / 1024 ** 2).toFixed(1)} Mo`;
  return `${(value / 1024 ** 3).toFixed(2)} Go`;
};

const date = (value) => {
  const d = value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
};

const iconOf = (item) => {
  const mime = item.copy_mime || item.mime || '';
  if (item.kind === 'folder') return Folder;
  if (item.kind === 'link') return Link2;
  if (mime.startsWith('image/')) return Image;
  if (mime.startsWith('video/')) return Film;
  if (mime.startsWith('audio/')) return Music;
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime === 'text/csv') return FileSpreadsheet;
  if (mime.includes('presentation') || mime.includes('powerpoint')) return Presentation;
  if (mime === 'application/pdf' || mime.includes('document') || mime.startsWith('text/')) return FileText;
  return File;
};

const STATE = {
  pending: { label: 'copie en cours', className: 'bg-amber-50 text-amber-700', icon: Clock },
  error: { label: 'copie impossible', className: 'bg-rose-50 text-rose-700', icon: AlertTriangle },
  upload: { label: 'en attente d’envoi au Drive', className: 'bg-sky-50 text-sky-700', icon: Clock },
};

const fileUrl = (projectId, folderId) => `/projects/${projectId}/files${folderId ? `?folder_id=${encodeURIComponent(folderId)}` : ''}`;

const linkOf = (projectId, item, mode) => apiService.get(`/projects/${projectId}/files/${encodeURIComponent(item.id)}/link?mode=${mode}`);

const download = async (projectId, item) => {
  const link = await linkOf(projectId, item, 'download');
  if (!link?.url) throw new Error('Fichier pas encore copié.');
  const a = document.createElement('a');
  a.href = link.url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
};

const Preview = ({ projectId, item, onClose }) => {
  const [link, setLink] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let alive = true;
    linkOf(projectId, item, item.native ? 'preview' : 'view')
      .then((value) => { if (alive) setLink(value); })
      .catch((err) => { if (alive) setError(err.message); });
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { alive = false; window.removeEventListener('keydown', onKey); };
  }, [projectId, item, onClose]);
  const type = link?.content_type || '';
  let body = <Loader2 className="h-6 w-6 animate-spin text-white/70" />;
  if (error) body = <p className="text-sm text-white">{error}</p>;
  else if (link?.url) {
    if (type.startsWith('image/')) body = <img src={link.url} alt={item.name} className="max-h-full max-w-full rounded-lg bg-white object-contain" />;
    else if (type.startsWith('video/')) body = <video src={link.url} controls autoPlay className="max-h-full max-w-full rounded-lg bg-black" />;
    else if (type.startsWith('audio/')) body = <audio src={link.url} controls autoPlay className="w-full max-w-xl" />;
    else if (type === 'application/pdf' || type.startsWith('text/')) {
      body = <iframe title={item.name} src={link.url} className="h-full w-full rounded-lg bg-white" />;
    } else {
      body = (
        <div className="rounded-xl bg-white p-6 text-center text-sm text-slate-600">
          <p>Pas d’aperçu pour ce format.</p>
          <button type="button" onClick={() => download(projectId, item).catch((err) => setError(err.message))}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white">
            <Download className="h-3.5 w-3.5" /> Télécharger
          </button>
        </div>
      );
    }
  }
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-950/85 p-3 sm:p-6" role="dialog" aria-label={item.name} onClick={onClose}>
      <div className="mb-3 flex items-center gap-2 text-white" onClick={(event) => event.stopPropagation()}>
        <p className="min-w-0 flex-1 truncate text-sm font-semibold">{item.name}</p>
        <button type="button" title="Télécharger" onClick={() => download(projectId, item).catch((err) => setError(err.message))}
          className="rounded-lg p-2 hover:bg-white/10"><Download className="h-4 w-4" /></button>
        {item.web_link && (
          <a href={item.web_link} target="_blank" rel="noopener noreferrer" title="Ouvrir dans Google Drive"
            className="rounded-lg p-2 hover:bg-white/10"><ExternalLink className="h-4 w-4" /></a>
        )}
        <button type="button" title="Fermer" onClick={onClose} className="rounded-lg p-2 hover:bg-white/10"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center" onClick={(event) => event.stopPropagation()}>{body}</div>
    </div>
  );
};

const ProjectFilesPanel = ({ projectId, project, maxHeight = '70vh' }) => {
  const [folderId, setFolderId] = useState(null);
  const [data, setData] = useState(null);
  const [fallback, setFallback] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [preview, setPreview] = useState(null);
  const [uploads, setUploads] = useState([]);
  const input = useRef(null);

  const load = useCallback(async (target) => {
    setLoading(true);
    setError('');
    try {
      const result = await apiService.get(fileUrl(projectId, target));
      if (!result?.mirrored) setFallback(true);
      else setData(result);
    } catch (err) {
      if (err.status === 409) setFallback(true);
      else setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(folderId); }, [load, folderId]);

  const open = (item) => {
    if (item.kind === 'folder') { setFilter(''); setFolderId(item.id); return; }
    if (item.kind === 'link' || item.state !== 'ok') { if (item.web_link) window.open(item.web_link, '_blank', 'noopener'); return; }
    if (item.preview) setPreview(item);
    else download(projectId, item).catch((err) => setError(err.message));
  };

  const send = async (files) => {
    const target = data?.folder_id;
    for (const file of Array.from(files || [])) {
      const key = `${file.name}-${Date.now()}`;
      setUploads((list) => [...list, { key, name: file.name, percent: 0 }]);
      try {
        const form = new FormData();
        form.append('file', file);
        await apiService.uploadFile(fileUrl(projectId, target), form,
          (percent) => setUploads((list) => list.map((u) => (u.key === key ? { ...u, percent } : u))));
      } catch (err) {
        setError(`${file.name} : ${err.message || 'envoi impossible'}`);
      } finally {
        setUploads((list) => list.filter((u) => u.key !== key));
      }
    }
    load(target === data?.root?.id ? null : target);
  };

  if (fallback) return <DriveBrowserPanel projectId={projectId} project={project} maxHeight={maxHeight} />;

  const items = (data?.items || []).filter((item) => !filter || item.name.toLowerCase().includes(filter.toLowerCase()));
  const crumbs = data ? [{ id: null, name: data.root.name }, ...data.path] : [];
  const rootLink = data?.root?.id ? `https://drive.google.com/drive/folders/${data.root.id}` : null;

  return (
    <div className="space-y-3"
      onDragOver={(event) => { if (data) event.preventDefault(); }}
      onDrop={(event) => { if (data && event.dataTransfer?.files?.length) { event.preventDefault(); send(event.dataTransfer.files); } }}>
      <div className="flex flex-wrap items-center gap-2">
        <nav className="flex min-w-0 flex-1 flex-wrap items-center gap-1 text-sm">
          {crumbs.map((crumb, index) => (
            <React.Fragment key={crumb.id || 'racine'}>
              {index > 0 && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}
              <button type="button" onClick={() => { setFilter(''); setFolderId(crumb.id); }}
                className={`truncate rounded px-1 ${index === crumbs.length - 1 ? 'font-semibold text-slate-800' : 'text-slate-500 hover:text-slate-800'}`}>
                {crumb.name}
              </button>
            </React.Fragment>
          ))}
        </nav>
        <label className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Filtrer ce dossier"
            className="w-40 rounded-lg border border-slate-200 py-1.5 pl-7 pr-2 text-xs outline-none focus:border-slate-400" />
        </label>
        <button type="button" onClick={() => input.current?.click()} disabled={!data}
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-40">
          <Upload className="h-3.5 w-3.5" /> Déposer
        </button>
        <input ref={input} type="file" multiple className="hidden" onChange={(event) => { send(event.target.files); event.target.value = ''; }} />
        <button type="button" title="Rafraîchir" onClick={() => load(folderId)} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
        {rootLink && (
          <a href={rootLink} target="_blank" rel="noopener noreferrer" title="Ouvrir le dossier dans Google Drive"
            className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"><ExternalLink className="h-4 w-4" /></a>
        )}
      </div>

      {error && <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</p>}
      {uploads.map((u) => (
        <div key={u.key} className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <div className="flex justify-between"><span className="truncate">Envoi de {u.name}</span><span>{Math.round(u.percent)} %</span></div>
          <div className="mt-1 h-1 rounded bg-slate-200"><div className="h-1 rounded bg-slate-800" style={{ width: `${u.percent}%` }} /></div>
        </div>
      ))}

      <div className="overflow-y-auto rounded-xl border border-slate-100" style={{ maxHeight }}>
        {loading && !data ? (
          <div className="space-y-2 p-3">{[0, 1, 2, 3].map((i) => <div key={i} className="h-8 animate-pulse rounded bg-slate-100" />)}</div>
        ) : items.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-slate-400">{filter ? 'Aucun fichier ne correspond.' : 'Aucun fichier dans ce dossier.'}</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((item) => {
              const Icon = iconOf(item);
              const state = STATE[item.state];
              return (
                <li key={item.id} className="group flex items-center gap-3 px-3 py-2 hover:bg-slate-50">
                  <Icon className={`h-4 w-4 shrink-0 ${item.kind === 'folder' ? 'text-amber-500' : 'text-slate-400'}`} />
                  <button type="button" onClick={() => open(item)} className="min-w-0 flex-1 truncate text-left text-sm text-slate-700 hover:text-slate-950">
                    {item.name}
                  </button>
                  {state && (
                    <span title={item.error || ''} className={`hidden shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] sm:inline-flex ${state.className}`}>
                      <state.icon className="h-3 w-3" /> {state.label}
                    </span>
                  )}
                  {item.kind === 'link' && <span className="hidden shrink-0 text-[11px] text-slate-400 sm:inline">dans Google seulement</span>}
                  <span className="hidden w-16 shrink-0 text-right text-xs text-slate-400 md:inline">{item.kind === 'folder' ? '' : size(item.size)}</span>
                  <span className="hidden w-24 shrink-0 text-right text-xs text-slate-400 md:inline">{date(item.modified_time)}</span>
                  <span className="flex shrink-0 items-center gap-0.5 opacity-60 group-hover:opacity-100">
                    {item.kind === 'file' && item.state === 'ok' && item.preview && (
                      <button type="button" title="Aperçu" onClick={() => setPreview(item)} className="rounded p-1 text-slate-500 hover:bg-slate-200"><Eye className="h-3.5 w-3.5" /></button>
                    )}
                    {item.kind === 'file' && item.state === 'ok' && (
                      <button type="button" title="Télécharger" onClick={() => download(projectId, item).catch((err) => setError(err.message))}
                        className="rounded p-1 text-slate-500 hover:bg-slate-200"><Download className="h-3.5 w-3.5" /></button>
                    )}
                    {item.web_link && (
                      <a href={item.web_link} target="_blank" rel="noopener noreferrer" title="Ouvrir dans Google Drive"
                        className="rounded p-1 text-slate-500 hover:bg-slate-200"><ExternalLink className="h-3.5 w-3.5" /></a>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <p className="text-[11px] text-slate-400">
        Copie du Drive dans le stockage Innowide (Paris), mise à jour toutes les 5 minutes. Un fichier déposé ici part aussi dans le Drive.
        Les Docs, Sheets et Slides se modifient toujours dans Google Drive.
      </p>
      {preview && <Preview projectId={projectId} item={preview} onClose={() => setPreview(null)} />}
    </div>
  );
};

export default ProjectFilesPanel;
